import React from 'react';
import { Box, Button, Chip, Container, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { useNavigate } from 'react-router-dom';
import RealtimeMarketPanel from './RealtimeMarketPanel';

dayjs.locale('ko');

type Props = {
  apiBaseUrl?: string;
  pollingMs?: number;
  pageSize?: number;
};

const neo = {
  text: '#eaf2ff',
  muted: '#9fb4d8',
  soft: '#7f9cc3',
  border: 'rgba(120,140,180,.22)',
};

const chipPositiveSx = {
  color: '#79f3d2',
  background: 'rgba(14, 116, 144, 0.18)',
  border: '1px solid rgba(45, 212, 191, 0.20)',
  fontWeight: 900,
};

const chipNegativeSx = {
  color: '#ff8ca6',
  background: 'rgba(127, 29, 29, 0.18)',
  border: '1px solid rgba(251, 113, 133, 0.18)',
  fontWeight: 900,
};

function getSessionLabel() {
  const now = dayjs();
  const day = now.day();
  if (day === 0 || day === 6) return '휴장일';

  const hhmm = now.hour() * 100 + now.minute();
  if (hhmm < 900) return '장 시작 전';
  if (hhmm <= 1530) return 'KRX 장중';
  if (hhmm < 1540) return 'NXT 전환 구간';
  if (hhmm <= 2000) return 'NXT 애프터 · 스캐너는 KRX 마감 기준';
  return '장 종료 후';
}

export default function RealtimeStockScannerPage({
  apiBaseUrl = '/api/realtime/stocks',
  pollingMs = 5000,
  pageSize = 50,
}: Props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sessionLabel = getSessionLabel();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top, rgba(28,61,120,0.28) 0%, rgba(3,8,19,1) 36%), linear-gradient(180deg, #07111f 0%, #020816 100%)',
        color: neo.text,
        py: isMobile ? 1.2 : 2,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${neo.border}`,
              background: 'linear-gradient(180deg, rgba(10,24,45,0.92) 0%, rgba(7,18,34,0.92) 100%)',
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Typography sx={{ fontWeight: 900, color: neo.text, fontSize: 16 }}>
              PolicyIntel
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label="실시간 스캐너" size="small" sx={chipPositiveSx} />
              <Chip label={`시장 상태 · ${sessionLabel}`} size="small" sx={chipPositiveSx} />
              <Chip label="실시간 + 인텔리전스 차트 연결" size="small" sx={chipNegativeSx} />
            </Stack>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              p: 2,
              borderRadius: 2.25,
              border: `1px solid ${neo.border}`,
              background: 'linear-gradient(180deg, rgba(10,28,54,0.95) 0%, rgba(7,19,37,0.95) 100%)',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'flex-start',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  color: '#7dd3fc',
                  fontWeight: 900,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                PolicyIntel Live Scanner
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  fontSize: isMobile ? 26 : 40,
                  fontWeight: 900,
                  color: '#f8fbff',
                  lineHeight: 1.15,
                }}
              >
                실시간 종목 스캐너
              </Typography>

              <Typography
                sx={{
                  mt: 1.2,
                  color: neo.muted,
                  fontSize: 14,
                  lineHeight: 1.75,
                  maxWidth: 920,
                }}
              >
                이 화면은 최근 갱신 종목을 빠르게 비교하고, 거래대금·변동률·거래량 기준으로
                우선 확인할 종목을 선별하는 전용 화면입니다. 종목을 클릭하면 실시간 요약을 먼저
                확인하고, 필요 시 인텔리전스 차트로 이어집니다.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => navigate(0)}
              sx={{
                color: neo.text,
                borderColor: neo.border,
                borderRadius: 1.5,
                whiteSpace: 'nowrap',
                alignSelf: isMobile ? 'stretch' : 'flex-start',
                fontWeight: 900,
              }}
            >
              페이지 새로고침
            </Button>
          </Box>

          <RealtimeMarketPanel
            apiBaseUrl={apiBaseUrl}
            pollingMs={pollingMs}
            compact={false}
            maxRows={pageSize}
            defaultMarket="all"
            showHeader={false}
            showFilters={true}
          />
        </Stack>
      </Container>
    </Box>
  );
}
